/**
 * Shared dropdown, generalized from the `Selectish` defined locally in
 * screens/sendTest.tsx (git history: "Selectish dropdown via Modal — floats
 * correctly, single-open, outside-click closes"). That one only took plain
 * string options; this adds an optional {value,label} form for the
 * governance screens' selects (message class, approval state, channel,
 * permit/deny) without changing sendTest.tsx's own usage.
 *
 * RN has no native <select>; the open menu renders inside a Modal so it
 * escapes every parent stacking context and floats above everything, with a
 * transparent scrim that closes it on outside tap.
 */
import React, { useRef, useState } from 'react';
import { View, Text, Pressable, ScrollView, Modal, StyleSheet } from 'react-native';
import { useTheme } from '../../context/themeContext';
import Icon from './Icon';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string = string> {
  value: T;
  onChange: (v: T) => void;
  options: Array<T | SelectOption<T>>;
  width?: number;
  placeholder?: string;
  disabled?: boolean;
}

function normalize<T extends string>(options: Array<T | SelectOption<T>>): SelectOption<T>[] {
  return options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

export function Select<T extends string = string>({ value, onChange, options, width, placeholder, disabled }: SelectProps<T>) {
  const { theme: { colors } } = useTheme();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number } | null>(null);
  const triggerRef = useRef<View>(null);
  const normalized = normalize(options);
  const current = normalized.find((o) => o.value === value);

  const openMenu = () => {
    triggerRef.current?.measureInWindow((x, y, w, h) => {
      setAnchor({ x, y: y + h + 2, w });
      setOpen(true);
    });
  };

  return (
    <View style={{ width }}>
      <Pressable
        ref={triggerRef}
        disabled={disabled}
        onPress={() => (open ? setOpen(false) : openMenu())}
        style={[
          st.input,
          { borderColor: colors.line, backgroundColor: colors.paper, opacity: disabled ? 0.5 : 1 },
        ]}
      >
        <Text style={{ color: current ? colors.ink : colors.ink4, fontSize: 13 }} numberOfLines={1}>
          {current ? current.label : placeholder || 'Select…'}
        </Text>
        <Icon name="chevronDown" size={12} color={colors.ink3} />
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={{ flex: 1 }} onPress={() => setOpen(false)}>
          {anchor && (
            <View
              style={[st.dropdown, { backgroundColor: colors.card, borderColor: colors.line, top: anchor.y, left: anchor.x, width: anchor.w }]}
              onStartShouldSetResponder={() => true}
            >
              <ScrollView style={{ maxHeight: 240 }}>
                {normalized.map((o) => (
                  <Pressable key={o.value} onPress={() => { onChange(o.value); setOpen(false); }} style={st.dropdownRow}>
                    <Text style={{ color: o.value === value ? colors.accent : colors.ink2, fontSize: 13 }}>{o.label}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          )}
        </Pressable>
      </Modal>
    </View>
  );
}

const st = StyleSheet.create({
  input: {
    height: 36, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  },
  dropdown: {
    position: 'absolute', borderWidth: 1, borderRadius: 2,
    elevation: 8, shadowColor: '#101828', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.12, shadowRadius: 12,
  },
  dropdownRow: { paddingHorizontal: 12, paddingVertical: 9 },
});

export default Select;
